// SleeveHoldingEffects.jsx — DAS-3360: once a month, per account, which holdings made it beat or
// trail its benchmark. Shown under the gap chart on Portfolio Status. The effect of a holding is
// its weight at the month's start x (its return - the benchmark's return); the holdings, the cash
// and "trades, dividends and timing" add up to the account's gap (sleeve_holding_effects.py).
import React, { useEffect, useState } from 'react';
import api from '../functions/api';

const fmtDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
const pts = (v) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)} pts`;
const pct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const color = (v) => (v >= 0 ? '#2e7d32' : '#c62828');

export const AccountNote = ({ note, label, threshold }) => {
  const gap = note.gap_pts;
  return (
    <div data-testid={`holding-effects-${note.account}`} style={{ marginTop: 8, fontSize: 13 }}>
      <div>
        <b>{label || note.account}</b>
        {` — ${fmtDay(note.period_start)} to ${fmtDay(note.period_end)}: ${pct(note.own_pct)} against the benchmark's ${pct(note.bench_pct)}, `}
        {gap == null ? 'gap unknown' : (
          <span style={{ color: color(gap) }}>{`${Math.abs(gap).toFixed(2)} pts ${gap >= 0 ? 'ahead' : 'behind'}`}</span>
        )}
      </div>
      <div style={{ paddingLeft: 12, color: '#555' }}>
        {note.items.length === 0 && (
          <div>{`No single holding moved the gap by ${threshold} points or more.`}</div>
        )}
        {note.items.map((it) => (
          <div key={it.ticker}>
            <span style={{ color: color(it.effect_pts) }}>{pts(it.effect_pts)}</span>
            {` ${it.company || it.ticker} (${pct(it.weight_pct)} of the account, ${it.ticker === '__CASH__' ? 'earning nothing' : `${pct(it.return_pct)} this month`})`}
          </div>
        ))}
        {note.trades_pts != null && (
          <div>
            <span style={{ color: color(note.trades_pts) }}>{pts(note.trades_pts)}</span>
            {' trades, dividends and timing'}
            {note.sold.length > 0 ? ` (sold during the month: ${note.sold.join(', ')})` : ''}
          </div>
        )}
      </div>
    </div>
  );
};

const SleeveHoldingEffects = ({ labels }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/sleeve_holding_effects')
      .then((res) => { if (!cancelled) setData(res.data || null); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  const notes = (data && Array.isArray(data.notes)) ? data.notes : [];
  const threshold = (data && data.threshold_pts) || 0.5;
  return (
    <div data-testid="sleeve-holding-effects" style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: '#999' }}>Last month, per account: the holdings that moved the gap</div>
      {notes.length === 0 ? (
        <span style={{ color: '#999', fontSize: 13 }}>
          {data && data.first_snapshot
            ? `The first note appears after next month's copy of the holdings (first copy taken ${fmtDay(data.first_snapshot)}).`
            : 'The monthly note starts with the first copy of the holdings, taken on the first night of a month.'}
        </span>
      ) : notes.map((n) => (
        <AccountNote key={n.account} note={n} label={(labels || {})[n.account]} threshold={threshold} />
      ))}
    </div>
  );
};

export default SleeveHoldingEffects;
